import { Fragment, type JSX, type ReactNode } from "react";

import { Link, useLocation } from "react-router";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useRouteResources } from "@/hooks/use-route-resources";
import { routeBreadcrumbs } from "@/lib/navigation";

/**
 * Breadcrumb labels, the route table behind them and the Overview root crumb
 * live in `@/lib/navigation`, shared with the sidebar and the shortcut overlay.
 */
export interface AppHeaderProps {
  /** Page title slot, rendered as the page's single `h1`. */
  readonly title?: ReactNode;
  /** Page actions slot, pinned to the inline end of the header. */
  readonly actions?: ReactNode;
}

/** Hidden while the header is narrower than 36rem; see the trail below. */
const NARROW_HIDDEN = "@max-xl/app-header:hidden";

export function AppHeader({ title, actions }: AppHeaderProps): JSX.Element {
  const location = useLocation();
  // Project and agent crumbs show the resource's name when the shell's own
  // queries have it, and the route identifier when they do not.
  const resources = useRouteResources(location.pathname);
  const crumbs = routeBreadcrumbs(location.pathname, resources.names);
  const lastIndex = crumbs.length - 1;

  return (
    /*
      One fixed 48px row (#163 R7): nothing in it wraps. The trail and the title
      give way first — they truncate — while the trigger and the actions keep
      their size, so the header neither grows nor pushes the actions off the
      inline end. Its gutter matches `main`'s, so the trail lines up with the
      page content below it.

      What fits depends on the header's own width, not the viewport's: the
      desktop sidebar takes 256px from 768px up, so a 768px window leaves a
      narrower header than a 700px one. `app-header` is the container the trail
      and the metric chips (`shell-metrics.tsx`) size themselves against.
    */
    <header
      className="@container/app-header sticky top-0 z-10 flex h-12 shrink-0 items-center gap-3 border-b bg-background px-4 xl:px-6"
      data-slot="app-header"
    >
      <SidebarTrigger className="-ml-1 shrink-0" />
      {/* Centred on the row: the primitive's own `self-stretch` pins a fixed-height rule to its top. */}
      <Separator className="h-4 data-vertical:self-center" orientation="vertical" />
      <Breadcrumb className="min-w-0">
        <BreadcrumbList>
          {crumbs.map(function crumbItem(crumb, index): ReactNode {
            /*
              A header narrower than 36rem (a phone, or a tablet beside the
              expanded sidebar) has room for the current page and little else,
              so its ancestors — and every separator with them — step aside
              there rather than each truncating to an ellipsis. The sidebar and
              the page tabs still lead back up.
            */
            const narrowHidden = index === lastIndex ? undefined : NARROW_HIDDEN;
            return (
              <Fragment key={`${String(index)}-${crumb.label}`}>
                {index > 0 && <BreadcrumbSeparator className={NARROW_HIDDEN} />}
                {/* The full label as a tooltip, for a crumb truncated to fit the row. */}
                <BreadcrumbItem className={narrowHidden} title={crumb.label}>
                  {crumb.href !== undefined && (
                    <BreadcrumbLink render={<Link to={crumb.href} />}>{crumb.label}</BreadcrumbLink>
                  )}
                  {/* The current page is the only crumb that carries aria-current. */}
                  {crumb.href === undefined && index === lastIndex && (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  )}
                  {/* An ancestor with no route of its own: plain text, not a link. */}
                  {crumb.href === undefined && index !== lastIndex && (
                    <span className="truncate">{crumb.label}</span>
                  )}
                </BreadcrumbItem>
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      {title !== undefined && <h1 className="min-w-0 truncate text-page-title">{title}</h1>}
      <div className="ml-auto flex shrink-0 items-center justify-end gap-2">{actions}</div>
    </header>
  );
}
